import type { QueryClient } from "@tanstack/react-query";
import type { RecipeDraftApprovalResult, RecipeDraftRow } from "@workspace/api-client-react";

export const APPROVAL_BATCH_SIZE = 100;

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const formatGBP = (v: string | number) => gbp.format(Number(v));

export function formatSignedGBP(v: string | null): string {
  if (v === null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  if (Math.round(n * 100) === 0) return "£0.00";
  return `${n > 0 ? "+" : "-"}${gbp.format(Math.abs(n))}`;
}

export const STATUS_LABELS: Record<RecipeDraftRow["status"], string> = {
  not_drafted: "Not drafted",
  draft: "Draft",
  approved: "Approved",
  rejected: "Skipped",
  redraft_requested: "Redraft requested",
};

export function isBalanced(row: Pick<RecipeDraftRow, "sumDifference" | "invalid">): boolean {
  return !row.invalid && row.sumDifference !== null && Math.round(Number(row.sumDifference) * 100) === 0;
}

/** Client-side hint only; the server is authoritative and reports its own skips. */
export function approvalBlocker(row: RecipeDraftRow): string | null {
  if (row.status === "approved" || row.hasLiveRecipe) return "Already live";
  if (!row.draftId || row.status === "not_drafted") return "No draft";
  if (row.generationState === "queued" || row.generationState === "running") return "Drafting";
  if (row.invalid) return "Invalid draft";
  if (row.status !== "draft" || row.generationState !== "done") return "Not ready for approval";
  if (row.isStale) return "Stale - price changed, redraft";
  if (!isBalanced(row)) return "Unbalanced";
  return null;
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function asApprovalResult(err: unknown): RecipeDraftApprovalResult | null {
  const data = err && typeof err === "object" ? (err as { data?: unknown }).data : null;
  if (data && typeof data === "object" && "details" in data && "approved" in data && "skipped" in data) {
    return data as RecipeDraftApprovalResult;
  }
  return null;
}

/** Invalidate staging list/detail plus every library and recipe cache (user + admin master list). */
export function invalidateAfterPublish(qc: QueryClient) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey[0];
      return typeof k === "string" && (k.startsWith("/api/cost-library") || k.startsWith("/api/admin/cost-library"));
    },
  });
}

export function invalidateDrafts(qc: QueryClient) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey[0];
      return typeof k === "string" && k.startsWith("/api/admin/cost-library/recipe-drafts");
    },
  });
}
